import { ITitleDescription } from "../Interfaces/base";
import { TServices, TSubservices } from "../Interfaces/constants";
import { IVideoBanner } from "../Interfaces/shared";

const verifyIdIcon: string = require("../assets/images/services/verify-id-icon.svg").default;
const greyVerifyIcon: string = require("../assets/images/services/grey-verify-id-icon.svg").default;
const eSignIcon: string = require("../assets/images/services/eSign-icon.svg").default;
const wetSignIcon: string = require("../assets/images/services/wet-sign-icon.svg").default;
const notaryServiceIcon: string = require("../assets/images/services/notary-icon.svg").default;
const rushIcon: string = require("../assets/images/services/rush-icon.svg").default;
const coSignerIcon: string = require("../assets/images/services/co-signers-icon.svg").default;
const sealedIcon: string = require("../assets/images/services/seald-icon.svg").default;
const agentReviewIcon: string = require("../assets/images/services/agent-border-icon.svg").default;
const inStorePic: string = require("../assets/images/services/instoreVerifyId.png").default;
const remotePic: string = require("../assets/images/services/remoteVerifyId.png").default;
const rushPic: string = require("../assets/images/services/rushPic.png").default;
const serviceVideo: string = require("../assets/video/services.mp4").default;
const serviceVideoMobile: string = require("../assets/video/mobile/services.mp4").default;

export const seo: ITitleDescription = {
  title: 'Complete signing solutions for car dealerships | Mavsign',
  description: 'Fuel the success of your auto business with our specialized services. Tap into the power of eSign, identity validation, and eSeal for all your offsite deals.',
}

export const videoBanner: IVideoBanner = {
  title: 'Streamline your process',
  description: 'Mavsign is a full-service mobile notary signing company specialized in the automotive industry. We make outside dealership purchases easy and safe. Our network of over 10,000 certified notaries prevents any risk of fraud through our Customer Validation system. Rest assured that all contract signings are given thorough attention, uncovering any Red Flags or identity theft attempts',
  video: {
    desktop: serviceVideo,
    mobile: serviceVideoMobile,
  },
  eventAction: 'request_demo_event_services',
  eventLabel: 'Request Demo Button - Services',
  altText: 'The automotive platform trusted for eSiging, identity check, eSeal, wet signature, notary services, rush orders and agent review',
}

export const services: TServices = {
  verifyId: {
    icon: {
      src: verifyIdIcon,
      alt: 'Verify Id'
    },
    title: 'Verify ID',
    subtitle: 'Security and risk mitigation against fraudulent transactions and identity theft.',
    description: `VerifyID is the <b>quick and safe</b> path to <b>proper client identity validation</b>. Our app cross-checks biometric facial features and ID photos with governmental databases to <b>uncover any inconsistencies that may lead to fraud attempts</b>. The process results in a Pass or Fail outcome, allowing you to <b>make fast informed decisions</b>. <b>Pass</b> results enable you to proceed the car purchase transaction without unnecessary delays, knowing that no fraudulent attempts have been made by the prospective buyer. <b>Fail</b> results are escalated to our <b>team of forensic
      experts</b> who manually review each case.<br/><b>Supported documents:</b> Driver's Licenses & Passports.`
  },
  eSign: {
    icon: {
      src: eSignIcon,
      alt: 'E Sign'
    },
    title: 'E-sign',
    subtitle: 'Convenience and efficiency; anytime, from anywhere.',
    description: `Say goodbye to the physical limitations of your dealership and prepare for a nationwide expansion. Our eSign service allows you to <b>sign contracts with clients nationwide</b>. The Mavsign digital signing solution removes paper documents from the equation. In return you get a <b>seamless transaction</b> where car purchases and contract signings are just a few clicks away. The eSign process is very straightforward: you send the documents to the relevant parties. They review and sign them on their digital
    devices and send them back to you. <b>No paperwork, no manual data entry.</b> Just a fast, safe, and convenient way to move inventory.`
  },
  wetSign: {
    icon: {
      src: wetSignIcon,
      alt: 'Wet Sign'
    },
    title: 'Wet Sign',
    subtitle: 'Fast and efficient transactions with traditional signatures',
    description: `The Mavsign wet signing system is a well-oiled process that allows car contracts to leave the dealership, arrive at their clients and return with remarkable efficiency and security. Our <b>trusted tradition in remote signing</b> allows us to provide a flawless experience for both dealers and clients. It starts at your dealership, where you put together the required documentation and upload it on the Mavsign platform. Your clients or our remote notaries can access and print it. The wet signed documents can be either uploaded back on the platform or delivered back to you. It's fast, secure, and always accurate.`
  },
  notaryServices: {
    icon: {
      src: notaryServiceIcon,
      alt: 'Notary Service'
    },
    title: 'Notary Services',
    subtitle: "Your dealership's legal liaison in every US state",
    description: `When physical documents require notarization, Mavsign's Notary Service is here to deliver <b>quick and risk-free results</b>. Our nationwide certified mobile notary network is ready to assist your dealership with all document signings. Notaries receive your documents either online or by delivery. Our MavOps team connects them to your client, where the notary witnesses the signing while ensuring it follows your specific <b>Red Flag compliance protocol.</b> The notary sends back to you the <b>completed</b> documents so that the car purchase transaction can safely proceed. Our top priorities are aligned with those of our mobile notaries: to deliver <b>fast, complete, and accurate document signings</b>. Average processing time: 3 to 5 business days. Fast-track option with Rush Order to 24 - 48 hours.`
  },
  CoSigner: {
    icon: {
      src: coSignerIcon,
      alt: 'co-signer'
    },
    title: 'Co-signers',
    subtitle: 'Secondary endorsements signings made easy',
    description: `A single individual may find themselves in the situation where they cannot shoulder the weight of an automotive transaction and document signing alone. It's where our Co-signers service comes into play. It is crafted to  <b>simplify</b> and <b>expedite</b> the process when <b>dual signatures</b> are essential. All Mavsign orders can be complemented with the Co-signers feature. Coupled with our existing services, this add-on gives both signatories the same <b>flexibility</b> to move forward with their car purchase transaction. To include this option in your orders simply designate both parties that will co-sign your document and invite them to the signing (either through secure online links or in person). Once all signatures are in place, the transaction can proceed confidently.`
  },
  sealed: {
    icon: {
      src: sealedIcon,
      alt: 'sealed'
    },
    title: 'Sealed',
    subtitle: 'The Mavsign seal on all electronic documents',
    description: `The Mavsign Seal is our latest security improvement. It <b>attests the authenticity of eSignatures</b> and completed contracts by sealing the documents. This seal <b>disables any future editing</b>. By using this service, you ensure the <b>integrity</b> of your signed documents, enhance their <b>legal admissibility</b>, and get a comprehensive <b>audit trail</b> of every document seal. With Sealed you can rest assured that your critical data remains shielded against any potential threats, while enhancing the <b>trust</b> and credibility of your dealership. Available for all Mavsign orders.`
  },
  agent: {
    icon: {
      src: agentReviewIcon,
      alt: 'agent review'
    },
    title: 'Agent Review',
    subtitle: '',
    description: `In the dynamic landscape of automotive transactions and document validation, preserving the <b>integrity of essential documents</b> is paramount. We introduce the "Agent Review" service, meticulously crafted to address the critical elements of <b>Document Authenticity</b>, <b>Document Originality</b>, and <b>Document Validity</b> upon request. "Agent Review" is your safeguard against any potential discrepancies or fraudulent attempts. When triggered by our vigilant Fraud team, this service ensures that our expert agents verify each document, leaving no room for doubt. Any anomalies or irregularities are identified and addressed promptly, providing you with the assurance that all documents <b>meet the highest standards of security.</b>`
  },
}

export const subservices: TSubservices = {
  inStore: {
    picture: {
      src: inStorePic,
      alt: "In-store clients shaking hand with dealer after identity check"
    },
    icon: {
      src: greyVerifyIcon,
      alt: 'Verify Id'
    },
    title: 'In-store orders',
    description: `Mavsign allows your dealership representatives to make <b>seamless in-person verifications for all walk-in customers</b>. Compliance with regulatory requirements is critical for in-store transactions. VerifyID ensures that your dealership adheres to these regulations, reducing the risk of legal complications.`  
  },
  remote: {
    picture: {
      src: remotePic,
      alt: "Remote client doing identity check for out of state car purchase"
    },
    icon: {
      src: greyVerifyIcon,
      alt: 'Verify Id'
    },
    title: 'Remote orders',
    description: `VerifyID is <b>the easiest way to validate the identity of your remote clients</b>. They receive your secure link on their phones and complete the process in a couple of minutes. A Mavsign Scheduler checks each submission for errors to minimize remote transaction risks and ensure you are dealing with legitimate clients.`
  },
  rush: {
    picture: {
      src: rushPic,
      alt: "Happy customer hugging hew newly bought car"
    },
    icon: {
      src: rushIcon,
      alt: 'rush icon'
    },
    title: 'Rush orders',
    description: `Rush orders: our solution to meet your tight deadlines and expedite urgent transactions. Turn your clients' demands for swift solutions into <b>opportunities</b> to improve your customer satisfaction with Rush Orders. Yourorder becomes our top priority. With Rush Orders, in <b>24 to 48 hours</b> you will have your <b>complete documents</b> signed, authenticated, and witnessed by a Notary from our network, and sent back to your dealership. Gain the <b>competitive advantage</b> while keeping your standards and risk mitigation at a constant high. At Mavsign speed never compromises quality. Average processing time: 24 - 48 hours.`
  },
}
